// MapView — wrapper de Leaflet (vanilla, sin react-leaflet)
import { useEffect, useRef } from "react";
import L from "leaflet";

// Fix icons default
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function makeMarkerIcon(emoji, kind = "") {
  return L.divIcon({
    html: `<div class="marker-bubble ${kind}">${emoji}</div>`,
    className: "marker-wrap",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

function makeUserIcon() {
  return L.divIcon({
    html: `<div class="marker-user"></div>`,
    className: "marker-wrap",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

export default function MapView({
  center = [47.4979, 19.0402],
  zoom = 13,
  markers = [],          // [{ id, lat, lng, emoji, kind, name, onClick }]
  userPosition = null,   // { lat, lng }
  routeToActive = null,  // { from: {lat,lng}, to: {lat,lng} }
  activeId = null,
  fitBounds = false,
  height = "100%",
  onMarkerClick,
  className = "",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const userMarkerRef = useRef(null);
  const routeLineRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom,
      zoomControl: true,
      attributionControl: true,
    });
    // Tiles OpenStreetMap (gratis, sin API key)
    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        subdomains: "abc",
        maxZoom: 19,
      }
    ).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
    // eslint-disable-next-line
  }, []);

  // Update center/zoom when changed
  useEffect(() => {
    if (mapRef.current && !fitBounds) {
      mapRef.current.setView(center, zoom, { animate: true });
    }
  }, [center[0], center[1], zoom, fitBounds]); // eslint-disable-line

  // Sync markers
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const seen = new Set();
    markers.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      seen.add(m.id);
      const isActive = m.id === activeId;
      const icon = makeMarkerIcon(m.emoji || "📍", `${m.kind || ""} ${isActive ? "active" : ""}`);
      if (markersRef.current[m.id]) {
        markersRef.current[m.id].setLatLng([m.lat, m.lng]);
        markersRef.current[m.id].setIcon(icon);
      } else {
        const mk = L.marker([m.lat, m.lng], { icon }).addTo(map);
        if (m.name) mk.bindPopup(`<strong>${m.name}</strong>`);
        if (onMarkerClick) mk.on("click", () => onMarkerClick(m.id));
        markersRef.current[m.id] = mk;
      }
    });
    // Cleanup removed
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    if (fitBounds && markers.length > 1) {
      const bounds = L.latLngBounds(markers.filter(m => m.lat != null).map((m) => [m.lat, m.lng]));
      // Solo incluir user si está razonablemente cerca (< 50km del centro de los markers)
      if (userPosition && bounds.isValid()) {
        const c = bounds.getCenter();
        const distKm = map.distance([userPosition.lat, userPosition.lng], [c.lat, c.lng]) / 1000;
        if (distKm < 50) bounds.extend([userPosition.lat, userPosition.lng]);
      }
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [markers, activeId, fitBounds]); // eslint-disable-line

  // User marker
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    if (userPosition) {
      if (userMarkerRef.current) {
        userMarkerRef.current.setLatLng([userPosition.lat, userPosition.lng]);
      } else {
        userMarkerRef.current = L.marker([userPosition.lat, userPosition.lng], {
          icon: makeUserIcon(),
          interactive: false,
          zIndexOffset: 1000,
        }).addTo(map);
      }
    } else if (userMarkerRef.current) {
      map.removeLayer(userMarkerRef.current);
      userMarkerRef.current = null;
    }
  }, [userPosition]);

  // Route line
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    if (routeLineRef.current) {
      map.removeLayer(routeLineRef.current);
      routeLineRef.current = null;
    }
    if (routeToActive && routeToActive.from && routeToActive.to) {
      routeLineRef.current = L.polyline(
        [[routeToActive.from.lat, routeToActive.from.lng], [routeToActive.to.lat, routeToActive.to.lng]],
        { color: "#d2691e", weight: 3, dashArray: "6 8", opacity: 0.85 }
      ).addTo(map);
    }
  }, [routeToActive]);

  return <div ref={containerRef} className={className} style={{ height, width: "100%" }} data-testid="map-view" />;
}
